<script lang="ts" setup>
import { inject, ref, watch } from 'vue';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import ToggleBox from '@/components/ToggleBox.vue';

import type { Ref } from 'vue';
import type { IManifest } from '@/types/IManifest';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { ManifestHandler } from '@/viewerExtensions';
import type { Lights } from '@/viewerExtensions';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const fastRewind = ref<boolean>(manifest.value.fastRewind ?? false);
const hideFloor = ref<boolean>(manifest.value.hideFloor ?? false);
const manifestHandlerExtension = viewer.value?.extensions.get('ManifestHandler') as Extension<ManifestHandler>;
const lightsExtension = viewer.value?.extensions.get('Lights') as Extension<Lights>

watch(fastRewind, (newValue: boolean) => {
  manifest.value.fastRewind = newValue;

  if (manifestHandlerExtension.sceneJson) {
    manifestHandlerExtension.sceneJson.fastRewind = newValue;
  }
});

watch(hideFloor, (newValue: boolean) => {
  manifest.value.hideFloor = newValue;

  if (manifestHandlerExtension.sceneJson) {
    manifestHandlerExtension.sceneJson.hideFloor = newValue;
  }

  if (newValue === false) {
    lightsExtension.createGround();
  } else {
    lightsExtension.destroyGround();
  }
});

watch(() => manifest.value.hideFloor, (newValue) => {
  if (newValue === undefined) return;

  hideFloor.value = newValue;
});
</script>

<template>
  <CollapseFieldset :enable-collapse="false">
    <template #legend>
      General Configuration
    </template>

    <div class="flex flex-col mb-1 last-of-type:mb-0">
      <label for="project-name" class="text-xs">Project Name</label>
      <input id="project-name" v-model="manifest.name" type="text" class="border p-1 text-xs">
    </div>

    <div class="flex flex-col mb-1  last-of-type:mb-0">
      <label for="base-part-path" class="text-xs">Base Part Path</label>
      <input id="base-part-path" v-model="manifest.basePath" type="text" class="border p-1 text-xs">
    </div>

    <div v-if="manifest.hideXButton === false" class="flex flex-col mb-1 last-of-type:mb-0">
      <label for="x-button" class="text-xs">X Button URL</label>
      <input id="x-button" v-model="manifest.xButtonURL" type="text" class="border p-1 text-xs">
    </div>

    <div class="flex flex-col mb-1 last-of-type:mb-0">
      <ToggleBox v-model="manifest.hideXButton" label="Hide X Button" @change="manifest.xButtonURL = ''" />
    </div>

    <div class="flex flex-col mb-1 last-of-type:mb-0">
      <ToggleBox v-model="fastRewind" label="Fast Rewind Model Animations" />
    </div>

    <div class="flex flex-col mb-1 last-of-type:mb-0">
      <ToggleBox v-model="hideFloor" label="Hide Default Floor" />
    </div>
  </CollapseFieldset>
</template>
